import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';

import DatePickerApp from '../../../components/UI/DatePicker';
import { useGetMyWarningsQuery } from '../../../redux/apis/attendanceViolationApi';
import ViolationTimeMeta from '../../Admin/AttendanceViolation/components/ViolationTimeMeta';
import { ViolationWidgetSkeleton } from '../../Admin/AttendanceViolation/components/ViolationSkeletons';
import {
  formatViolationDateLong,
  getSortedViolations,
  getViolationTypeLabel,
  getViolationWarningNumber,
  getWarningStatusConfig,
  normalizeMyWarningsResponse
} from '../../../utils/attendanceViolationHelpers';
// import EmptyData from '../../../components/UI/EmptyData';
import '../../Admin/AttendanceViolation/AttendanceViolation.scss';
import './MyWarningsWidget.scss';

const FILTER_ALL = 'all';
const VIOLATION_FILTER_TYPES = ['LATE_ARRIVAL', 'EARLY_EXIT'];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const getViolationStatusKey = (violation = {}) => {
  if (violation?.leaveApplied) return 'LEAVE_APPLIED';

  const warningStatus = String(violation?.warningStatus || '');
  if (warningStatus && warningStatus !== 'HR_NOTIFIED') {
    return warningStatus;
  }

  const count = getViolationWarningNumber(violation) ?? Number(violation?.monthlyCount);
  if (count === 1) return '1/2';
  if (count === 2) return '2/2';

  return null;
};

const buildDateNum = (year, month, day) =>
  Number(`${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`);

const StatusBadge = ({ status }) => {
  const config = getWarningStatusConfig(status);
  return (
    <span
      className={`att-violation-status att-violation-status--${config.color} att-violation-status--sm`}
    >
      {config.label}
    </span>
  );
};

const TOOLTIP_GAP = 12;

const getPopoverAlign = (colIndex) => {
  if (colIndex <= 1) return 'start';
  if (colIndex >= 5) return 'end';
  return 'center';
};

const getPopoverPlacement = (rowIndex) => (rowIndex >= 3 ? 'top' : 'bottom');

const getTooltipPosition = (anchor, placement, align) => {
  const centerX = anchor.left + anchor.width / 2;

  if (placement === 'bottom') {
    if (align === 'start') {
      return { top: anchor.bottom + TOOLTIP_GAP, left: anchor.left };
    }
    if (align === 'end') {
      return {
        top: anchor.bottom + TOOLTIP_GAP,
        left: anchor.right,
        transform: 'translateX(-100%)'
      };
    }
    return {
      top: anchor.bottom + TOOLTIP_GAP,
      left: centerX,
      transform: 'translateX(-50%)'
    };
  }

  if (align === 'start') {
    return { top: anchor.top - TOOLTIP_GAP, left: anchor.left, transform: 'translateY(-100%)' };
  }
  if (align === 'end') {
    return {
      top: anchor.top - TOOLTIP_GAP,
      left: anchor.right,
      transform: 'translate(-100%, -100%)'
    };
  }
  return {
    top: anchor.top - TOOLTIP_GAP,
    left: centerX,
    transform: 'translate(-50%, -100%)'
  };
};

const WarningTooltip = ({ violations, anchor, placement, align }) => {
  if (!violations?.length || !anchor) return null;

  const positionStyle = getTooltipPosition(anchor, placement, align);

  return createPortal(
    <div
      className={`my-warnings-tooltip my-warnings-tooltip--${placement} my-warnings-tooltip--${align}`}
      style={{ position: 'fixed', zIndex: 1080, ...positionStyle }}
      role="tooltip"
    >
      <span className="my-warnings-tooltip__arrow" aria-hidden />
      <div className="my-warnings-tooltip__stack">
        {violations.map((violation) => {
          const statusKey = getViolationStatusKey(violation);
          const typeClass = violation?.violationType === 'EARLY_EXIT' ? 'exit' : 'late';

          return (
            <article
              key={
                violation?.violationId ||
                `${violation?.date}-${violation?.violationType}-${violation?.monthlyCount}`
              }
              className={`my-warnings-tooltip__card my-warnings-tooltip__card--${typeClass}`}
            >
              <header className="my-warnings-tooltip__head">
                <div className="my-warnings-tooltip__head-main">
                  <span
                    className={`my-warnings-tooltip__type my-warnings-tooltip__type--${typeClass}`}
                  >
                    {getViolationTypeLabel(violation?.violationType)}
                  </span>
                  <p className="my-warnings-tooltip__date">
                    {formatViolationDateLong(violation?.date)}
                  </p>
                </div>
                {statusKey ? <StatusBadge status={statusKey} /> : null}
              </header>
              <ViolationTimeMeta violation={violation} />
            </article>
          );
        })}
      </div>
    </div>,
    document.body
  );
};

const buildCalendarGrid = (year, month) => {
  const first = moment({ year, month: month - 1, day: 1 }).utcOffset(330);
  const daysInMonth = first.daysInMonth();
  const startDow = first.day();
  const cells = [];

  const prevMonth = first.clone().subtract(1, 'month');
  const prevDays = prevMonth.daysInMonth();
  for (let i = startDow - 1; i >= 0; i -= 1) {
    const day = prevDays - i;
    const m = prevMonth.month() + 1;
    const y = prevMonth.year();
    cells.push({
      day,
      month: m,
      year: y,
      dateNum: buildDateNum(y, m, day),
      isCurrentMonth: false,
      isFirstOfMonth: day === 1,
      colIndex: cells.length % 7
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      day,
      month,
      year,
      dateNum: buildDateNum(year, month, day),
      isCurrentMonth: true,
      isFirstOfMonth: day === 1,
      colIndex: cells.length % 7
    });
  }

  const nextMonth = first.clone().add(1, 'month');
  let nextDay = 1;
  while (cells.length % 7 !== 0) {
    const m = nextMonth.month() + 1;
    const y = nextMonth.year();
    cells.push({
      day: nextDay,
      month: m,
      year: y,
      dateNum: buildDateNum(y, m, nextDay),
      isCurrentMonth: false,
      isFirstOfMonth: nextDay === 1,
      colIndex: cells.length % 7
    });
    nextDay += 1;
  }

  while (cells.length < 42) {
    const m = nextMonth.month() + 1;
    const y = nextMonth.year();
    cells.push({
      day: nextDay,
      month: m,
      year: y,
      dateNum: buildDateNum(y, m, nextDay),
      isCurrentMonth: false,
      isFirstOfMonth: nextDay === 1,
      colIndex: cells.length % 7
    });
    nextDay += 1;
  }

  return cells;
};

const MiniCalendar = ({ cells, violationsByDate, todayDateNum, activeFilter }) => {
  const [tooltip, setTooltip] = useState(null);

  const showTooltip = useCallback((violations, event, colIndex, rowIndex, dateNum) => {
    if (!violations.length) return;
    setTooltip({
      violations,
      dateNum,
      anchor: event.currentTarget.getBoundingClientRect(),
      placement: getPopoverPlacement(rowIndex),
      align: getPopoverAlign(colIndex)
    });
  }, []);

  const hideTooltip = useCallback(() => setTooltip(null), []);

  useEffect(() => {
    if (!tooltip) return undefined;

    const dismiss = () => setTooltip(null);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);

    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [tooltip]);

  return (
    <>
      <div className="my-warnings-cal">
        <div className="my-warnings-cal__weekdays" aria-hidden>
          {WEEKDAYS.map((label) => (
            <span key={label} className="my-warnings-cal__weekday">
              {label}
            </span>
          ))}
        </div>

        <div className="my-warnings-cal__grid" role="grid" aria-label="Monthly warnings calendar">
          {cells.map((cell, index) => {
            const entry = violationsByDate.get(String(cell.dateNum));
            const violations = entry?.violations || [];
            const isActive = violations.length > 0;
            const toneClass =
              entry?.hasLate && entry?.hasExit
                ? ' my-warnings-cal__cell--mixed'
                : entry?.hasExit
                  ? ' my-warnings-cal__cell--exit'
                  : entry?.hasLate
                    ? ' my-warnings-cal__cell--late'
                    : '';
            const isToday = cell.dateNum === todayDateNum;
            const rowIndex = Math.floor(index / 7);
            const monthShort = moment({ year: cell.year, month: cell.month - 1 }).format('MMM');
            const isTooltipOpen = tooltip?.dateNum === cell.dateNum;

            return (
              <div
                key={`${cell.dateNum}-${cell.isCurrentMonth ? 'cur' : 'adj'}`}
                className={`my-warnings-cal__cell${
                  cell.isCurrentMonth ? '' : ' my-warnings-cal__cell--muted'
                }${isToday ? ' my-warnings-cal__cell--today' : ''}${
                  isActive ? ' my-warnings-cal__cell--active' : ''
                }${toneClass}`}
                role="gridcell"
              >
                <span
                  className={`my-warnings-cal__day${isActive ? ' my-warnings-cal__day--hit' : ''}${
                    isTooltipOpen ? ' my-warnings-cal__day--open' : ''
                  }`}
                  tabIndex={isActive ? 0 : -1}
                  aria-label={
                    isActive
                      ? `Warning on ${formatViolationDateLong(cell.dateNum)}`
                      : `${cell.isFirstOfMonth ? `${monthShort} ` : ''}${cell.day}`
                  }
                  onMouseEnter={(event) =>
                    showTooltip(violations, event, cell.colIndex, rowIndex, cell.dateNum)
                  }
                  onMouseLeave={hideTooltip}
                  onFocus={(event) =>
                    showTooltip(violations, event, cell.colIndex, rowIndex, cell.dateNum)
                  }
                  onBlur={hideTooltip}
                >
                  <span className="my-warnings-cal__day-num">
                    {cell.isFirstOfMonth ? (
                      <span className="my-warnings-cal__day-month">{monthShort},</span>
                    ) : null}
                    <span className="my-warnings-cal__day-value">{cell.day}</span>
                  </span>

                  {isActive ? (
                    <span className="my-warnings-cal__dots" aria-hidden>
                      {violations.map((violation) => {
                        const warningNumber =
                          getViolationWarningNumber(violation) ??
                          (Number(violation?.monthlyCount) || null);
                        const typeClass =
                          violation?.violationType === 'EARLY_EXIT' ? 'exit' : 'late';
                        const showLate =
                          typeClass === 'late' &&
                          (activeFilter === FILTER_ALL || activeFilter === 'LATE_ARRIVAL');
                        const showExit =
                          typeClass === 'exit' &&
                          (activeFilter === FILTER_ALL || activeFilter === 'EARLY_EXIT');

                        if (!showLate && !showExit) return null;

                        return (
                          <span
                            key={
                              violation?.violationId ||
                              `${violation?.date}-${violation?.violationType}-${warningNumber}`
                            }
                            className={`my-warnings-cal__dot my-warnings-cal__dot--${typeClass}${
                              warningNumber ? '' : ' my-warnings-cal__dot--plain'
                            }`}
                            title={
                              warningNumber
                                ? `Warning ${warningNumber}`
                                : getViolationTypeLabel(violation?.violationType)
                            }
                          >
                            {warningNumber ? (
                              <span className="my-warnings-cal__dot-num">{warningNumber}</span>
                            ) : null}
                          </span>
                        );
                      })}
                    </span>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {tooltip ? <WarningTooltip {...tooltip} /> : null}
    </>
  );
};

const getIstNow = () => moment().utcOffset(330);

const getIstMonthYear = () => {
  const now = getIstNow();
  return {
    month: now.month() + 1,
    year: now.year()
  };
};

const MonthFilter = ({ month, year, onChange, compact = false }) => {
  const istNow = getIstNow();
  const monthMoment = useMemo(
    () => moment({ year, month: month - 1 }).utcOffset(330),
    [month, year]
  );
  const isCurrentMonth = month === istNow.month() + 1 && year === istNow.year();

  const shiftMonth = (direction) => {
    if (direction > 0 && isCurrentMonth) return;
    const next = monthMoment.clone().add(direction, 'month');
    onChange(next.month() + 1, next.year());
  };

  const handleMonthPick = (picked) => {
    if (!picked) return;
    const next = moment(picked).utcOffset(330);
    if (next.isAfter(istNow, 'month')) return;
    onChange(next.month() + 1, next.year());
  };

  return (
    <div
      className={`my-warnings-widget__month-filter${compact ? ' my-warnings-widget__month-filter--compact' : ''}`}
    >
      <button
        type="button"
        className="my-warnings-widget__month-nav my-warnings-widget__month-nav--prev"
        aria-label="Previous month"
        onClick={() => shiftMonth(-1)}
      >
        <i className="bi bi-chevron-left" aria-hidden />
      </button>

      <div className="my-warnings-widget__month-picker">
        <div className="my-warnings-widget__month-picker-input">
          <DatePickerApp
            type="Month"
            initialDate={monthMoment}
            handleChangeDate={handleMonthPick}
            maxMonth={istNow}
          />
        </div>
      </div>

      <button
        type="button"
        className="my-warnings-widget__month-nav my-warnings-widget__month-nav--next"
        aria-label="Next month"
        disabled={isCurrentMonth}
        onClick={() => shiftMonth(1)}
      >
        <i className="bi bi-chevron-right" aria-hidden />
      </button>
    </div>
  );
};

const MyWarningsWidget = () => {
  const initialPeriod = useMemo(() => getIstMonthYear(), []);
  const [month, setMonth] = useState(initialPeriod.month);
  const [year, setYear] = useState(initialPeriod.year);
  const [activeFilter, setActiveFilter] = useState(FILTER_ALL);
  const istNow = getIstNow();
  const isCurrentMonth = month === istNow.month() + 1 && year === istNow.year();
  const todayDateNum = isCurrentMonth ? buildDateNum(year, month, istNow.date()) : null;

  const handleMonthChange = useCallback((nextMonth, nextYear) => {
    const selected = moment({ year: nextYear, month: nextMonth - 1 }).utcOffset(330);
    const now = getIstNow();
    if (selected.isAfter(now, 'month')) {
      setMonth(now.month() + 1);
      setYear(now.year());
    } else {
      setMonth(nextMonth);
      setYear(nextYear);
    }
    setActiveFilter(FILTER_ALL);
  }, []);

  const { data, isLoading, isFetching } = useGetMyWarningsQuery({ month, year });
  const warnings = useMemo(() => normalizeMyWarningsResponse(data), [data]);

  const summaries = useMemo(
    () =>
      warnings.summaries.filter(
        (summary) =>
          (summary?.monthlyViolationCount || 0) > 0 ||
          (Array.isArray(summary?.violations) && summary.violations.length > 0)
      ),
    [warnings.summaries]
  );

  const orderedSummaries = useMemo(() => {
    const order = ['LATE_ARRIVAL', 'EARLY_EXIT'];
    return [...summaries].sort(
      (a, b) => order.indexOf(a.violationType) - order.indexOf(b.violationType)
    );
  }, [summaries]);

  const filteredSummaries = useMemo(() => {
    if (activeFilter === FILTER_ALL) return orderedSummaries;
    return orderedSummaries.filter((summary) => summary.violationType === activeFilter);
  }, [orderedSummaries, activeFilter]);

  const violationsByDate = useMemo(() => {
    const map = new Map();

    filteredSummaries.forEach((summary) => {
      const violationType = summary?.violationType;
      getSortedViolations(summary).forEach((violation) => {
        const key = String(violation?.date || '');
        if (!key) return;

        const entry = map.get(key) || {
          violations: [],
          hasLate: false,
          hasExit: false
        };

        const enriched = { ...violation, violationType };
        entry.violations.push(enriched);
        if (violationType === 'EARLY_EXIT') entry.hasExit = true;
        else entry.hasLate = true;
        map.set(key, entry);
      });
    });

    return map;
  }, [filteredSummaries]);

  const calendarCells = useMemo(() => buildCalendarGrid(year, month), [year, month]);

  const filterOptions = useMemo(() => {
    const summaryByType = new Map(
      orderedSummaries.map((summary) => [summary.violationType, summary])
    );

    const items = [{ id: FILTER_ALL, label: 'All', count: warnings.totalWarnings }];

    VIOLATION_FILTER_TYPES.forEach((type) => {
      const summary = summaryByType.get(type);
      items.push({
        id: type,
        label: getViolationTypeLabel(type),
        count: summary ? (summary?.monthlyViolationCount ?? getSortedViolations(summary).length) : 0
      });
    });

    return items;
  }, [orderedSummaries, warnings.totalWarnings]);

  const showSkeleton = isLoading || (isFetching && !data);
  const monthLabel = new Date(year, month - 1, 1).toLocaleString(undefined, {
    month: 'long',
    year: 'numeric'
  });
  const hasWarnings = orderedSummaries.length > 0;

  return (
    <div className="card my-warnings-widget h-100">
      {showSkeleton ? (
        <div className="my-warnings-widget__head my-warnings-widget__head--skeleton">
          <div>
            <span className="att-violation-skeleton__bar att-violation-skeleton__bar--title" />
          </div>
          <span className="att-violation-skeleton__bar att-violation-skeleton__bar--subtitle" />
        </div>
      ) : (
        <div className="my-warnings-widget__head">
          <div className="my-warnings-widget__title-block">
            <h2 className="my-warnings-widget__title">My Attendance Warnings</h2>
          </div>

          <MonthFilter month={month} year={year} onChange={handleMonthChange} compact />
        </div>
      )}

      <div className="my-warnings-widget__body">
        {showSkeleton ? (
          <ViolationWidgetSkeleton rows={4} />
        ) : (
          <div className="my-warnings-widget__content">
            <div className="my-warnings-widget__toolbar">
              <div
                className="my-warnings-widget__filters"
                role="tablist"
                aria-label="Warning type filter"
              >
                {filterOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="tab"
                    aria-selected={activeFilter === option.id}
                    className={`my-warnings-widget__filter my-warnings-widget__filter--${option.id.toLowerCase()}${
                      activeFilter === option.id ? ' my-warnings-widget__filter--active' : ''
                    }`}
                    onClick={() => setActiveFilter(option.id)}
                  >
                    <span>{option.label}</span>
                    <span className="my-warnings-widget__filter-count">{option.count}</span>
                  </button>
                ))}
              </div>

              <div className="my-warnings-widget__toolbar-meta">
                <div className="my-warnings-cal__legend" aria-hidden>
                  <span className="my-warnings-cal__legend-item">
                    <span className="my-warnings-cal__legend-swatch my-warnings-cal__legend-swatch--late" />
                    Late Arrival
                  </span>
                  <span className="my-warnings-cal__legend-item">
                    <span className="my-warnings-cal__legend-swatch my-warnings-cal__legend-swatch--exit" />
                    Early Exit
                  </span>
                </div>
              </div>
            </div>

            <MiniCalendar
              cells={calendarCells}
              violationsByDate={violationsByDate}
              todayDateNum={todayDateNum}
              activeFilter={activeFilter}
            />

            {!hasWarnings ? (
              <div className="my-warnings-widget__empty my-warnings-widget__empty--inline">
                <p className="my-warnings-widget__empty-text">No warnings for {monthLabel}</p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyWarningsWidget;
